/** @jsxImportSource @emotion/react */
import { Card, Flex, Label, Text } from "@jsq/ds/core";
import {
  FormCheckbox,
  FormDatePicker,
  FormRadioButton,
  FormRadioGroup,
  FormTextInput,
  FormTextarea,
  useFormContext,
} from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { FormValues } from "../types";

interface Props {
  camelCasedRoles:
    | {
        name: string;
        __typename?: "RoleObjectType" | undefined;
        id: string;
        description: string | null;
        isManageBasicConfigurationSetting: boolean;
        isManageStaffAccessSetting: boolean;
        isManageAdminConfigurationSetting: boolean;
        isObjectLevelBasicPermission: boolean;
        isNonStaffRole: boolean;
      }[]
    | undefined;
}

const DraftAnnouncementCard = (props: Props) => {
  const { values } = useFormContext<FormValues>();

  const camelCasedRoles = props.camelCasedRoles || [];

  return (
    <Card title="Draft announcement">
      <FormTextInput
        label="Announcement name (internal only)"
        name="name"
        data-testid="name"
        isFullWidth
        css={{ marginBottom: SPACING.MEDIUM }}
        placeholder="Add a name"
      />
      <div css={{ marginBottom: SPACING.MEDIUM }} data-testid="date-field">
        <Label className="stacked-bottom-md">{"Expiration Date (optional)"}</Label>
        <Text css={{ marginBottom: SPACING.SMALL }} variant="secondary-sm">
          Defaults to 30 days from today
        </Text>
        <FormDatePicker name="expiryDate" aria-label="Expiration Date" />
      </div>
      <FormTextInput
        label="Subject line"
        name="subject"
        data-testid="subject"
        isFullWidth
        css={{ marginBottom: SPACING.MEDIUM }}
        placeholder="Add a subject"
      />
      <FormTextarea
        label="Body message"
        name="body"
        data-testid="body"
        isFullWidth
        css={{ marginBottom: SPACING.MEDIUM }}
      />

      <FormTextInput
        label="Hyperlink (optional)"
        name="hyperlink"
        data-testid="hyperlink"
        isFullWidth
        css={{ marginBottom: SPACING.MEDIUM }}
        placeholder="http"
      />
      <FormRadioGroup style={{ marginBottom: SPACING.MEDIUM }} name="audience" label="Audience">
        <FormRadioButton value="all">All users</FormRadioButton>
        <FormRadioButton value="select">Select users</FormRadioButton>
      </FormRadioGroup>
      {values.audience === "select" && (
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          css={{ marginLeft: SPACING.MEDIUM }}
        >
          <FormCheckbox
            name="isObjectLevelBasicPermissionRole" // different from advanced permission
            label="Basic permissions (Account, Contact, Entity, Offerings, Prospects)"
          ></FormCheckbox>
          <FormCheckbox
            name="isManageAdminConfigurationSettingRole"
            label="Can manage administrator configuration settings"
          ></FormCheckbox>
          <FormCheckbox
            name="isManageStaffAccessSettingRole"
            label="Can manage staff access"
          ></FormCheckbox>
          <FormCheckbox
            name="isManageBasicSettingRole"
            label="Can manage basic configuration settings"
          ></FormCheckbox>
          {camelCasedRoles.map((role) => (
            <FormCheckbox
              key={role.id} // Unique key for each checkbox
              name={role.name ?? ""}
              label={role.description}
            />
          ))}
        </Flex>
      )}
    </Card>
  );
};

export default DraftAnnouncementCard;
